import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Users, CheckCircle, User, Briefcase, UserCheck } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecipientSelector({ onSelectionChange, campaignType }) {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, client, lead

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchTerm, filterType, clients]);

  useEffect(() => {
    // Notificar cambios al padre
    onSelectionChange(Array.from(selectedIds));
  }, [selectedIds]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Obtener clientes y leads
      const response = await axios.get(`${BACKEND_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo los que tienen email
      const withEmail = response.data.filter(c => c.email && c.email.trim() !== '');
      setClients(withEmail);
      setFilteredClients(withEmail);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar destinatarios');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = [...clients];

    // Filtrar por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(c => c.entity_type === filterType);
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(search) ||
        (c.email && c.email.toLowerCase().includes(search)) ||
        (c.phone && c.phone.includes(search)) ||
        (c.alias && c.alias.toLowerCase().includes(search))
      );
    }

    setFilteredClients(filtered);
  };

  const toggleSelection = (clientId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(filteredClients.map(c => c.id));
    setSelectedIds(allIds);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const getClientTypeInfo = (client) => {
    if (client.entity_type === 'lead') {
      return { icon: Briefcase, label: 'Lead', color: 'bg-orange-100 text-orange-800' };
    }
    return { icon: User, label: 'Cliente', color: 'bg-blue-100 text-blue-800' };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando destinatarios...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Seleccionar Destinatarios
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-indigo-600 border-indigo-600">
              {selectedIds.size} seleccionados
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Controles superiores */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Búsqueda */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, email, teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filtros de tipo */}
          <div className="flex gap-2">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('all')}
            >
              Todos ({clients.length})
            </Button>
            <Button
              variant={filterType === 'client' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('client')}
            >
              Clientes ({clients.filter(c => c.entity_type === 'client').length})
            </Button>
            <Button
              variant={filterType === 'lead' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('lead')}
            >
              Leads ({clients.filter(c => c.entity_type === 'lead').length})
            </Button>
          </div>
        </div>

        {/* Acciones masivas */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAll}
            className="gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Seleccionar Todos ({filteredClients.length})
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearSelection}
          >
            Limpiar Selección
          </Button>
        </div>

        {/* Lista de destinatarios */}
        <div className="border rounded-lg max-h-96 overflow-y-auto">
          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No se encontraron destinatarios</p>
              {searchTerm && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="mt-2"
                >
                  Limpiar búsqueda
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredClients.map((client) => {
                const typeInfo = getClientTypeInfo(client);
                const TypeIcon = typeInfo.icon;
                const isSelected = selectedIds.has(client.id);

                return (
                  <div
                    key={client.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => toggleSelection(client.id)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelection(client.id)}
                        className="mt-1"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <h4 className="font-medium text-gray-900 truncate">
                            {client.name}
                          </h4>
                          <Badge className={typeInfo.color} variant="secondary">
                            {typeInfo.label}
                          </Badge>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            📧 {client.email}
                          </p>
                          {client.phone && (
                            <p className="text-sm text-gray-600">
                              📱 {client.phone}
                            </p>
                          )}
                          {client.alias && (
                            <p className="text-sm text-gray-500 italic">
                              Alias: {client.alias}
                            </p>
                          )}
                        </div>

                        {client.entity_type === 'client' && (
                          <div className="mt-2 flex gap-3 text-xs text-gray-500">
                            <span>Pólizas activas: {client.active_policies || 0}</span>
                            <span>Total pólizas: {client.total_policies || 0}</span>
                          </div>
                        )}
                      </div>

                      {isSelected && (
                        <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Resumen */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Mostrando {filteredClients.length} de {clients.length} destinatarios
            </span>
            <span className="font-medium text-indigo-600">
              {selectedIds.size} seleccionado(s)
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
